import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Create axios instance for student auth
const studentAuthAPI = axios.create({
  baseURL: `${API_URL}/api/student-auth`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add token
studentAuthAPI.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('studentAuthToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
studentAuthAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Use the logout method for consistent cleanup
      studentAuthService.logout();

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/student/login')) {
        window.location.href = '/student/login';
      }
    }
    return Promise.reject(error);
  }
);

// Student Auth Service
const studentAuthService = {
  // Internal flag to prevent multiple logout calls
  _isLoggingOut: false,

  // Login
  async login(phoneNumber, password) {
    const response = await studentAuthAPI.post('/login', {
      phone_number: phoneNumber,
      password: password,
    });

    if (response.data.access_token) {
      // MUTUAL EXCLUSION: Clear any existing admin session
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');

      // Set student session
      localStorage.setItem('studentAuthToken', response.data.access_token);
      localStorage.setItem('studentUser', JSON.stringify(response.data.student));
      localStorage.setItem('mustChangePassword', response.data.must_change_password);
      localStorage.setItem('userType', 'student');
    }

    return response.data;
  },

  // Logout
  logout() {
    // Prevent multiple logout calls
    if (studentAuthService._isLoggingOut) return;
    studentAuthService._isLoggingOut = true;

    localStorage.removeItem('studentAuthToken');
    localStorage.removeItem('studentUser');
    localStorage.removeItem('mustChangePassword');
    localStorage.removeItem('userType');

    // Reset flag after a short delay
    setTimeout(() => {
      studentAuthService._isLoggingOut = false;
    }, 100);
  },

  // Check if authenticated
  isAuthenticated() {
    const token = localStorage.getItem('studentAuthToken');
    const userType = localStorage.getItem('userType');

    // Must have token AND be student userType
    if (!token || userType !== 'student') return false;

    try {
      // Parse JWT to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const isExpired = payload.exp * 1000 < Date.now();

      if (isExpired) {
        // Don't call logout here to avoid redirect loops
        localStorage.removeItem('studentAuthToken');
        localStorage.removeItem('studentUser');
        localStorage.removeItem('mustChangePassword');
        localStorage.removeItem('userType');
        return false;
      }

      return true;
    } catch (e) {
      // Invalid token format
      localStorage.removeItem('studentAuthToken');
      localStorage.removeItem('studentUser');
      localStorage.removeItem('mustChangePassword');
      localStorage.removeItem('userType');
      return false;
    }
  },

  // Get current student
  getCurrentStudent() {
    const student = localStorage.getItem('studentUser');
    return student ? JSON.parse(student) : null;
  },

  // Check if must change password
  mustChangePassword() {
    return localStorage.getItem('mustChangePassword') === 'true';
  },

  // Get student info
  async getStudentInfo() {
    const response = await studentAuthAPI.get('/me');
    // Update local storage
    localStorage.setItem('studentUser', JSON.stringify(response.data));
    return response.data;
  },

  // Update profile
  async updateProfile(data) {
    const response = await studentAuthAPI.put('/me', data);
    // Update local storage with new data
    if (response.data.student) {
      localStorage.setItem('studentUser', JSON.stringify(response.data.student));
    }
    return response.data;
  },

  // Change password
  async changePassword(oldPassword, newPassword, confirmPassword) {
    const response = await studentAuthAPI.post('/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    // Update must change password flag
    localStorage.setItem('mustChangePassword', 'false');
    return response.data;
  },

  // First login password change
  async firstLoginPasswordChange(newPassword, confirmPassword) {
    const response = await studentAuthAPI.post('/first-login-password-change', {
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    // Update must change password flag
    localStorage.setItem('mustChangePassword', 'false');
    return response.data;
  },

  // Forgot password
  async forgotPassword(phoneNumber) {
    const response = await studentAuthAPI.post('/forgot-password', {
      phone_number: phoneNumber,
    });
    return response.data;
  },

  // Verify OTP and reset password
  async verifyOTP(phoneNumber, otp, newPassword, confirmPassword) {
    const response = await studentAuthAPI.post('/verify-otp', {
      phone_number: phoneNumber,
      otp: otp,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  },

  // Get attendance
  async getAttendance(skip = 0, limit = 100, fromDate = null, toDate = null) {
    const params = { skip, limit };
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;

    const response = await studentAuthAPI.get('/attendance', { params });
    return response.data;
  },

  // Get tests
  async getTests(skip = 0, limit = 100) {
    const response = await studentAuthAPI.get('/tests', {
      params: { skip, limit }
    });
    return response.data;
  },

  // Get test details
  async getTestDetails(attemptId) {
    const response = await studentAuthAPI.get(`/test-details/${attemptId}`);
    return response.data;
  },

  // Get fees
  async getFees() {
    const response = await studentAuthAPI.get('/fees');
    return response.data;
  },

  // Profile picture methods
  async uploadProfilePicture(data) {
    const response = await studentAuthAPI.post('/profile-picture', data);
    return response.data;
  },

  async getProfilePicture() {
    const response = await studentAuthAPI.get('/profile-picture', {
      responseType: 'blob'
    });
    return response.data;
  },

  async deleteProfilePicture() {
    const response = await studentAuthAPI.delete('/profile-picture');
    return response.data;
  },

  // Get available tests for filter dropdown
  async getAvailableTests() {
    const response = await studentAuthAPI.get('/available-tests');
    return response.data;
  },

  // Get top performers
  async getTopPerformers(testId = null) {
    const params = testId ? { test_id: testId } : {};
    const response = await studentAuthAPI.get('/top-performers', { params });
    return response.data;
  },

  // Get my rank
  async getMyRank(testId = null) {
    const params = testId ? { test_id: testId } : {};
    const response = await studentAuthAPI.get('/my-rank', { params });
    return response.data;
  },
};

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    // If studentAuthToken was removed in another tab, redirect this tab too
    if (event.key === 'studentAuthToken' && event.newValue === null) {
      const currentUserType = localStorage.getItem('userType');
      // Only react if we were a student session
      if (currentUserType === 'student' || currentUserType === null) {
        if (!window.location.pathname.includes('/student/login')) {
          window.location.href = '/student/login';
        }
      }
    }
  });
}

export default studentAuthService;
